<script setup lang="ts">
import { mdToHtml } from '~/lib/markdown'
import {
  BlockAccordion,
  HtmlHeadings,
  TitleRenderAsType,
} from '~/lib/services/content/content.blocks.dtos'

const props = withDefaults(
  defineProps<{
    block: BlockAccordion
    structuredData?: boolean
    titleRenderAs?: TitleRenderAsType
    isFaqsHome?: boolean
  }>(),
  {
    structuredData: true,
    titleRenderAs: HtmlHeadings.H2,
  }
)

useJsonld(() => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': props.block.elements.map(el => ({
      '@type': 'Question',
      'name': el.title,
      'acceptedAnswer': { '@type': 'Answer', 'text': mdToHtml(el.text) },
    })),
  }
})

const domain = useSiteDomain({ protocol: false, noOverride: true })
const route = useRoute()

const isFaqsPage = computed(() => route.path.includes('/faqs'))

const createCustomUrl = (sectionTitle: string, blockTitle: string) => {
  if (!isFaqsPage.value) return undefined
  return `https://${domain}/faqs/${slugify(sectionTitle)}/${slugify(blockTitle)}`
}
</script>

<template>
  <div data-test="mcd-accordion" class="mx-auto my-4 md:px-14 lg:w-[800px] lg:px-0">
    <RenderTitleAs
      class="max-w-[70%] px-4 text-2xl font-bold md:px-0 lg:max-w-none lg:text-center lg:text-5xl"
      :text="props.block.title"
      :render-as="titleRenderAs"
    />
    <div class="mx-auto my-4 lg:my-10 lg:flex lg:flex-col lg:gap-6">
      <Collapsable
        v-for="(collapsable, i) in props.block.elements"
        :key="`collapsable_${i}`"
        :element="collapsable"
        :section-title="props.block.title"
        data-test="mcd-accordion__element"
        :is-faqs-home="isFaqsHome"
        :custom-url="createCustomUrl(props.block.title, collapsable.title)"
      />
    </div>
  </div>
</template>
